import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  Spinner,
  Box,
  Input,
  FormControl,
  FormLabel,
  NumberInput,
  NumberInputField,
  Flex,
  Button,
  Select,
  HStack,
  AlertDialog,
  AlertDialogBody,
  AlertDialogCloseButton,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  useDisclosure,
  Checkbox,
  Text,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  ModalFooter,
} from "@chakra-ui/react";
import "@cyntler/react-doc-viewer/dist/index.css";
import { fileTypeFromBuffer } from "file-type";
import { useApi } from "../../hooks/useApi";
import { DocDefKeyIdSummary, FileType, NotificationContextType, RescanStatus } from "../../types";
import { NotificationContext } from "../../components/NotificationPane";
import AsyncSelect from "react-select/async";
import BaseTemplate from "../../components/BaseTemplate";
import { useLocation } from "wouter";
import React from "react";
import Viewer from "./Viewer";
import RegexTester from "./RegexTester";
import KeyIdSelectorModal from "./KeyIdSelectorModal";
import { toIso, parsePeriodRange, parseAmountFromText } from "../../util/dateAmountParsing";
import { destructureDocPath } from "../../util/docPath";

type ViewerProps = {
  id: string;
};

type CrmOption = { label: string; value: string }; // label = name, value = id

type EditableSnapshot = {
  doc_from: string;
  doc_family: string;
  doc_ref: string;
  doc_related_ref: string;
  doc_date: string;
  doc_period_from: string;
  doc_period_to: string;
  doc_total_amt_val: string;
  supplier_acct_ref: string;
  account_id_crm: string;
};

export default function FileViewer({ id }: ViewerProps) {
  /* ─── 2. Download blob once we have dir & filename ─────── */
  const {
    downloadFile,
    updateFiles,
    getFile,
    getDocFamilyOptions,
    getDocFromOptions,
    getSuggestedAccounts,
    putDocDefKeyId,
    getDocDefKeyIds,
    rescanDoc,
    getRescanStatus,
  } = useApi();
  const [originalFileBlob, setOriginalFileBlob] = useState<Blob | null>(null);
  const [originalFileName, setOriginalFileName] = useState<string | null>(null);
  const [textFileBlob, setTextFileBlob] = useState<Blob | null>(null);
  const [textFileName, setTextFileName] = useState<string | null>(null);
  const [documentText, setDocumentText] = useState<string>("");
  const [isLoadingOriginal, setIsLoadingOriginal] = useState<boolean>(false);
  const [isLoadingText, setIsLoadingText] = useState<boolean>(false);
  // A failed download must not retry by itself: the load effects re-run whenever loading ends
  // with no blob, which turned one missing file into ~4 API calls a second (2026-09-25). Cleared
  // on a new document or a page reload, and when the user re-ticks "Show original" / "Show txt".
  const [originalLoadFailed, setOriginalLoadFailed] = useState<boolean>(false);
  const [textLoadFailed, setTextLoadFailed] = useState<boolean>(false);
  const [file, setFile] = useState<FileType>();
  const { pushNotification } =
    useContext<NotificationContextType | null>(NotificationContext)!;
  const [docFamilyOptions, setDocFamilyOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const [docFromOptions, setDocFromOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const [allDocFromOptions, setAllDocFromOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const [selectedCrm, setSelectedCrm] = useState<CrmOption | null>(null);
  const [periodPasteValue, setPeriodPasteValue] = useState<string>("");
  const [, navigate] = useLocation();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isKeyIdModalOpen, onOpen: onKeyIdModalOpen, onClose: onKeyIdModalClose } = useDisclosure();
  const cancelRef = React.useRef<HTMLButtonElement>(null);
  const docPath = file?.doc_path;
  const [customerSupplier, setCustomerSupplier] = useState<string>("");
  const editableSnapshotRef = useRef<EditableSnapshot | null>(null);
  const [pendingDiscardAction, setPendingDiscardAction] = useState<
    "close" | "next" | "refresh" | null
  >(null);
  // Rescan queues the document (doc.hashlib_do PORTAL_RESCAN). Where edb_sidecar is configured the
  // backend also has it filed at once, and the viewer polls /files/rescan_status until it is done
  // (startRescan); otherwise the maintenance job files it later. The last-scan time (parsedate --
  // NOT scantime, which is the arrival time) at the moment of queueing tells a later Refresh whether
  // that has happened: unchanged = still queued. Compared as the database's own value, never
  // against the browser clock (the SQL server runs on UTC).
  const [scantimeAtRescan, setScantimeAtRescan] = useState<string | null>(null);
  const [isRescanning, setIsRescanning] = useState<boolean>(false);
  // bumped on a new document and on unmount: a poll that sees it change stops
  const rescanRunRef = useRef<number>(0);
  // The pop-up while it runs: "working" (Claude is asked when no rule identifies the document yet,
  // i.e. doc_status 0-...), or "queued" when the sidecar could not take it now. Hide keeps polling.
  const [rescanPopup, setRescanPopup] = useState<"closed" | "working" | "queued">("closed");
  const [rescanAskingClaude, setRescanAskingClaude] = useState<boolean>(false);
  const [rescanStartedAt, setRescanStartedAt] = useState<number | null>(null);
  const [rescanElapsed, setRescanElapsed] = useState<number>(0);
  const scanTimeText = (value?: string | null) => (value ? String(value).replace("T", " ").slice(0, 19) : "-");
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);
  type ActiveRegexField =
    | "doc_from" | "doc_family" | "doc_ref" | "doc_date"
    | "doc_related_ref" | "doc_period_from" | "doc_period_to" | "doc_total_amt_val" | "supplier_acct_ref";

  const [activeRegexField, setActiveRegexField] = useState<ActiveRegexField | null>(null);
  const [docFromKey, setDocFromKey] = useState<string>("");
  const [docFamilyKey, setDocFamilyKey] = useState<string>("");
  const [docTemplate, setDocTemplate] = useState<string>("");
  const [docType, setDocType] = useState<string>("PDF");
  const [currentDocDef, setCurrentDocDef] = useState<DocDefKeyIdSummary | null>(null);

  const regexFieldMap = {
    doc_from: "doc_from_regex",
    doc_family: "doc_family_regex",
    doc_ref: "doc_ref_regex",
    doc_date: "doc_date_regex",
  } as const;

  const parseFieldMap = {
    doc_related_ref: "DOC_RELATED_REF",
    doc_period_from: "DOC_PERIOD_FROM",
    doc_period_to: "DOC_PERIOD_TO",
    doc_total_amt_val: "DOC_TOTAL_AMT",
    supplier_acct_ref: "SUPPLIER_ACCT_REF",
  } as const;

  const toggleRegex = (field: ActiveRegexField) =>
    setActiveRegexField((prev) => (prev === field ? null : field));

  const editDefLink = (field: ActiveRegexField) => (
    <Box as="span" fontSize="xs" color="blue.500" cursor="pointer" onClick={() => toggleRegex(field)}>
      Edit Rules
    </Box>
  );

  const regexSaveFor = (field: keyof typeof regexFieldMap) =>
    async (regexString: string, dateFunction?: string) => {
      await putDocDefKeyId({
        doc_from_key: docFromKey,
        doc_family_key: docFamilyKey,
        doc_template: docTemplate,
        doc_type: docType,
        [regexFieldMap[field]]: regexString,
        ...(dateFunction ? { doc_date_function: dateFunction } : {}),
      });
      setCurrentDocDef((prev) => prev
        ? { ...prev, [regexFieldMap[field]]: regexString, ...(dateFunction ? { doc_date_function: dateFunction } : {}) }
        : prev
      );
      setActiveRegexField(null);
    };

  // View toggles
  const [showOriginal, setShowOriginal] = useState<boolean>(true);
  const [showText, setShowText] = useState<boolean>(false);
  const [pdfPageInfo, setPdfPageInfo] = useState<{
    page: number;
    total: number;
  } | null>(null);

  const loadCrmAccountOptions = async (input: string): Promise<CrmOption[]> => {
    if (!input || input.trim().length < 2) return [];
    const matches = await getSuggestedAccounts(input.trim());
    return matches.map(({ account_name, account_id }) => ({
      label: account_name, // shows in menu & after selection
      value: account_id, // never shown, but stored
    }));
  };

  const keyFieldStyles = {
    bg: "blue.50",
    borderColor: "blue.200",
  } as const;

  const getDocFromPrefix = (value: string): "S-" | "C-" | null => {
    const trimmed = String(value ?? "").trim().toUpperCase();
    if (trimmed.startsWith("S-")) return "S-";
    if (trimmed.startsWith("C-")) return "C-";
    return null;
  };

  useEffect(() => {
    const normalized = (customerSupplier ?? "").trim().toLowerCase();
    const prefix =
      normalized === "supplier"
        ? "S-"
        : normalized === "customer"
          ? "C-"
          : "";

    if (!normalized) {
      setDocFromOptions(allDocFromOptions);
      return;
    }

    setDocFromOptions(
      allDocFromOptions.filter((option) =>
        String(option.value ?? "").trim().toUpperCase().startsWith(prefix)
      )
    );
  }, [customerSupplier, allDocFromOptions]);

  // The loader stores the bare supplier code ('CRO'); the Doc From options carry the party prefix
  // ('S-CRO', 'C-...'), so the select showed blank for every loader-set value. Show the matching
  // option -- or the stored value itself -- so the current value is always visible. Display only:
  // file.doc_from is saved exactly as it is unless the user picks another option.
  const docFromShown = useMemo(() => {
    const value = String(file?.doc_from ?? "").trim();
    if (!value || allDocFromOptions.some((o) => o.value === value)) return value;
    return allDocFromOptions.find((o) => o.value === `S-${value}` || o.value === `C-${value}`)?.value ?? value;
  }, [file?.doc_from, allDocFromOptions]);

  useEffect(() => {
    if (!docFromShown) return;

    const prefix = getDocFromPrefix(docFromShown);
    if (prefix === "S-" && customerSupplier !== "supplier") {
      setCustomerSupplier("supplier");
    }

    if (prefix === "C-" && customerSupplier !== "customer") {
      setCustomerSupplier("customer");
    }
  }, [docFromShown, customerSupplier]);

  useEffect(() => {
    const previous = document.title; // remember the old title
    document.title = "Edit Metadata"; // set the new one
    return () => {
      document.title = previous;
    }; // restore on unmount
  }, []);

  /**
   * ✅ FIX + FEATURE:
   * Prefill CRM select when the file loads:
   *   label = file.scan_filename
   *   value = file.account_id_crm
   *
   * Your previous effect depended on `selectedCrm` (and also re-set selectedCrm),
   * which is a bug and can cause loops / stale state.
   */
  useEffect(() => {
    if (!file) return;

    if (file.account_id_crm) {
      setSelectedCrm({
        label: file.account_name,
        value: file.account_id_crm,
      });
    } else {
      setSelectedCrm(null);
    }
  }, [file?.account_id_crm]);

  useEffect(() => {
    (async () => {
      try {
        const options = await getDocFromOptions();
        setAllDocFromOptions(options);
      } catch (error) {
        console.error("Error fetching document from options:", error);
      }
    })();
  }, [getDocFromOptions]);

  useEffect(() => {
    (async () => {
      try {
        const options = await getDocFamilyOptions();
        setDocFamilyOptions(options);
      } catch (error) {
        console.error("Error fetching document family options:", error);
      }
    })();
  }, [getDocFamilyOptions]);

  useEffect(() => {
    try {
      getFile(id).then((file) => {
        setFile(file);
      });
    } catch (error) {
      console.error("Error fetching file:", error);
    }
  }, [id, getFile]);

  const normalizeValue = (value: unknown): string => {
    if (value == null) return "";
    return String(value).trim();
  };

  const normalizeAmount = (value: unknown): string => {
    if (value == null || value === "") return "";
    const numeric = typeof value === "number"
      ? value
      : Number.parseFloat(String(value).replace(/,/g, ""));
    if (Number.isNaN(numeric)) return normalizeValue(value);
    return String(numeric);
  };

  const buildEditableSnapshot = (current: FileType): EditableSnapshot => ({
    doc_from: normalizeValue(current.doc_from),
    doc_family: normalizeValue(current.doc_family),
    doc_ref: normalizeValue(current.doc_ref),
    doc_related_ref: normalizeValue(current.doc_related_ref),
    doc_date: normalizeValue(current.doc_date),
    doc_period_from: normalizeValue(current.doc_period_from),
    doc_period_to: normalizeValue(current.doc_period_to),
    doc_total_amt_val: normalizeAmount(current.doc_total_amt_val),
    supplier_acct_ref: normalizeValue(current.supplier_acct_ref),
    account_id_crm: normalizeValue(current.account_id_crm),
  });

  const isDirty = useMemo(() => {
    if (!file || !editableSnapshotRef.current) return false;
    const current = buildEditableSnapshot(file);
    const original = editableSnapshotRef.current;
    return (
      current.doc_from !== original.doc_from ||
      current.doc_family !== original.doc_family ||
      current.doc_ref !== original.doc_ref ||
      current.doc_related_ref !== original.doc_related_ref ||
      current.doc_date !== original.doc_date ||
      current.doc_period_from !== original.doc_period_from ||
      current.doc_period_to !== original.doc_period_to ||
      current.doc_total_amt_val !== original.doc_total_amt_val ||
      current.supplier_acct_ref !== original.supplier_acct_ref ||
      current.account_id_crm !== original.account_id_crm
    );
  }, [file]);
  // read by startRescan's poll, which outlives the render it started in
  const isDirtyRef = useRef<boolean>(false);
  isDirtyRef.current = isDirty;

  const discardMessage =
    pendingDiscardAction === "next"
      ? "Are you sure you want to skip to the next record? Your changes will not be saved."
      : pendingDiscardAction === "refresh"
        ? "Refresh reloads the saved values. Are you sure? Your changes will not be saved."
        : "Are you sure you want to close? Your changes will not be saved.";

  // Re-read the document's row: once a queued RESCAN has run, its scan time and key values are new.
  const refreshFile = async () => {
    setIsRefreshing(true);
    try {
      const fresh = await getFile(id);
      editableSnapshotRef.current = buildEditableSnapshot(fresh); // reloaded values are the saved ones
      setFile(fresh);
      if (scantimeAtRescan !== null) {
        const ran = (fresh.parsedate ?? "") !== scantimeAtRescan;
        pushNotification(
          ran
            ? "The rescan has run - showing the new values."
            : "The rescan is queued but has not run yet - nothing has changed.",
          ran ? "success" : "info",
        );
      }
    } catch (error) {
      pushNotification("Failed to refresh the document.", "error");
      console.error("Refresh failed", error);
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleRefresh = () => {
    if (isDirty) {
      setPendingDiscardAction("refresh");
      onOpen();
      return;
    }
    refreshFile();
  };

  // Rescan. Always queued; when the backend says edb_sidecar is filing it now (`immediate`), a
  // pop-up waits while it runs -- "Asking Claude for help" when no rule identifies the document yet --
  // then the document is re-read here, unless there are unsaved edits, which a reload would throw
  // away. When the sidecar cannot take it: "Rescan has been queued" (the maintenance job files it).
  const RESCAN_POLL_MS = 2000;
  const RESCAN_WAIT_MS = 5 * 60 * 1000;     // the AI rule maker can take minutes on a new layout
  const startRescan = async () => {
    if (!file) return;
    if (isRescanning) {
      setRescanPopup("working");                                 // already running: show it again
      return;
    }
    const run = ++rescanRunRef.current;
    const sid = file.sid;
    const mine = () => run === rescanRunRef.current;             // false: another document, or closed
    setRescanAskingClaude(String(file.doc_status ?? "").startsWith("0-"));
    setRescanStartedAt(Date.now());
    setIsRescanning(true);
    setRescanPopup("working");
    const queuedOnly = () => {
      if (mine()) setRescanPopup("queued");
    };
    const closePopup = () => {
      if (mine()) setRescanPopup("closed");
    };
    try {
      let result;
      try {
        result = await rescanDoc(sid);
      } catch (error) {
        // The request often never leaves the browser: a sign-in token that could not be renewed
        // silently (MSAL) on a page open a while. Say what failed, so it can be told apart.
        closePopup();
        const why = error instanceof Error && error.message ? `: ${error.message.slice(0, 200)}` : "";
        pushNotification(`Could not queue the rescan${why}. Reload the page and try again.`, "error");
        console.error("Rescan failed", error);
        return;
      }
      setScantimeAtRescan(file.parsedate ?? "");
      if (!result.immediate) {
        queuedOnly();
        return;
      }
      const started = Date.now();
      while (Date.now() - started < RESCAN_WAIT_MS) {
        await new Promise((resolve) => setTimeout(resolve, RESCAN_POLL_MS));
        if (!mine()) return;
        let status: RescanStatus;
        try {
          status = await getRescanStatus(sid);
        } catch (error) {
          console.error("Rescan status failed", error);          // a blip: ask again next time
          continue;
        }
        if (!mine()) return;
        if (status.state === "failed") {
          closePopup();
          pushNotification("The rescan failed: the document's stored text is missing.", "error");
          return;
        }
        if (status.state === "unavailable" || status.state === "not queued") {
          queuedOnly();
          return;
        }
        if (status.state !== "done") continue;                   // queued / running
        const ai = status.ai_called_this_rescan
          ? status.ai_saved_key_id
            ? ` Claude wrote a new rule for this layout: ${status.ai_saved_key_id}.`
            : " Claude looked at this layout but saved no rule."
          : "";
        if (isDirtyRef.current) {
          closePopup();
          pushNotification(`The rescan has run.${ai} You have unsaved changes - press Refresh to see the new values.`, "info");
          return;
        }
        const fresh = await getFile(id);
        if (!mine()) return;
        editableSnapshotRef.current = buildEditableSnapshot(fresh); // reloaded values are the saved ones
        setFile(fresh);
        closePopup();
        pushNotification(`Rescanned - showing the new values.${ai}`, "success");
        return;
      }
      closePopup();
      pushNotification("Still rescanning - press Refresh in a few minutes.", "info");
    } catch (error) {
      closePopup();
      pushNotification("The rescan ran, but the document could not be reloaded - press Refresh.", "error");
      console.error("Rescan reload failed", error);
    } finally {
      if (mine()) setIsRescanning(false);
    }
  };

  // the pop-up's seconds counter, while a rescan runs
  useEffect(() => {
    if (!isRescanning || rescanStartedAt === null) return;
    setRescanElapsed(0);
    const timer = setInterval(() => setRescanElapsed(Math.round((Date.now() - rescanStartedAt) / 1000)), 1000);
    return () => clearInterval(timer);
  }, [isRescanning, rescanStartedAt]);

  useEffect(() => () => {
    rescanRunRef.current += 1;                                  // closed: stop any poll
  }, []);

  useEffect(() => {
    setOriginalFileBlob(null);
    setOriginalFileName(null);
    setTextFileBlob(null);
    setTextFileName(null);
    setDocumentText("");
    setIsLoadingOriginal(false);
    setIsLoadingText(false);
    setOriginalLoadFailed(false);
    setTextLoadFailed(false);
    setPeriodPasteValue("");
    setScantimeAtRescan(null);
    rescanRunRef.current += 1;                                  // a new document: stop any poll
    setIsRescanning(false);
    setRescanPopup("closed");
  }, [docPath]);

  useEffect(() => {
    if (!file) return;
    editableSnapshotRef.current = buildEditableSnapshot(file);

    if (file.key_id) {
      const [fromKey = "", familyKey = "", template = ""] = file.key_id.split("__");
      setDocFromKey(fromKey);
      setDocFamilyKey(familyKey);
      setDocTemplate(template);

      getDocDefKeyIds().then((defs) => {
        const match = defs.find((d) => d.key_id === file.key_id) ?? null;
        setCurrentDocDef(match);
      });
    } else {
      setCurrentDocDef(null);
    }
  }, [file?.sid, file?.key_id]);

  useEffect(() => {
    if (!docPath || !showOriginal || originalFileBlob || isLoadingOriginal || originalLoadFailed) return;

    const originalDocPath = docPath.replace(/\.txt$/i, "");
    const { drive, dir, filename } = destructureDocPath(originalDocPath);

    if (!dir || !filename) return;

    setIsLoadingOriginal(true);
    (async () => {
      try {
        const bytes = await downloadFile(drive, dir, filename);
        const blob = new Blob([bytes], {
          type: (await fileTypeFromBuffer(bytes))?.mime,
        });
        setOriginalFileBlob(blob);
        setOriginalFileName(filename);
      } catch (e) {
        setOriginalLoadFailed(true);
        pushNotification("Failed to download original file. Reload the page to retry.", "error");
        console.error("Download failed", e);
      } finally {
        setIsLoadingOriginal(false);
      }
    })();
  }, [
    docPath,
    showOriginal,
    originalFileBlob,
    isLoadingOriginal,
    originalLoadFailed,
    downloadFile,
    pushNotification,
  ]);

  useEffect(() => {
    const shouldLoadText = showText || activeRegexField !== null;
    if (!docPath || !shouldLoadText || textFileBlob || isLoadingText || textLoadFailed) return;

    const originalDocPath = docPath.replace(/\.txt$/i, "");
    const textDocPath = `${originalDocPath}.txt`;
    const { drive, dir, filename } = destructureDocPath(textDocPath);

    if (!dir || !filename) return;

    setIsLoadingText(true);
    (async () => {
      try {
        const bytes = await downloadFile(drive, dir, filename);
        const blob = new Blob([bytes], {
          type: (await fileTypeFromBuffer(bytes))?.mime,
        });
        setTextFileBlob(blob);
        setTextFileName(filename);
      } catch (e) {
        setTextLoadFailed(true);
        pushNotification("Failed to download text file. Reload the page to retry.", "error");
        console.error("Download failed", e);
      } finally {
        setIsLoadingText(false);
      }
    })();
  }, [
    docPath,
    showText,
    activeRegexField,
    textFileBlob,
    isLoadingText,
    textLoadFailed,
    downloadFile,
    pushNotification,
  ]);

  useEffect(() => {
    if (!textFileBlob) return;

    let isCancelled = false;
    textFileBlob
      .text()
      .then((text) => {
        if (!isCancelled) {
          setDocumentText(text);
        }
      })
      .catch((error) => {
        console.error("Failed to read text file.", error);
      });

    return () => {
      isCancelled = true;
    };
  }, [textFileBlob]);

  async function next() {
    if (!file) return;

    const raw = localStorage.getItem("viewer-queue");
    if (!raw) {
      pushNotification("No viewer queue found.", "warning");
      return;
    }

    const ids: Array<string | number> = JSON.parse(raw);

    // ensure both sides are comparable as strings
    const currentId = String(id).trim();
    const idx = ids.findIndex((v) => String(v).trim() === currentId);

    if (idx === -1) {
      // current id isn’t in the queue – bailout or handle as you like
      pushNotification("ID not in queue; reload list?", "warning");
      return;
    }

    const nextId = ids[idx + 1];

    if (nextId !== undefined) {
      navigate(`/file_viewer/${nextId}`);
    } else {
      // reached the end
      localStorage.removeItem("viewer-queue");
      window.close();
    }
  }

  async function saveAndNext() {
    if (!file) return;

    try {
      const success = await updateFiles([file]);
      if (success) {
        next();
      } else {
        pushNotification("Failed to save file metadata.", "error");
      }
    } catch (error) {
      pushNotification("Failed to save file metadata.", "error");
    }
  }

  const handleFieldChange = (field: keyof FileType, value: any) => {
    setFile((prev) => (prev ? { ...prev, [field]: value } : prev));
  };

  const saveAndClose = async () => {
    if (!file) return;

    try {
      const success = await updateFiles([file]);
      if (success) {
        window.close();
      } else {
        pushNotification("Failed to save file metadata.", "error");
      }
    } catch (error) {
      pushNotification("Failed to save file metadata.", "error");
    }
  };

  const close = () => {
    if (isDirty) {
      setPendingDiscardAction("close");
      onOpen();
      return;
    }

    window.close();
  };

  const handleShowOriginalChange = (checked: boolean) => {
    if (!checked && !showText) return;
    if (checked) setOriginalLoadFailed(false);   // re-ticking is the user asking to retry
    setShowOriginal(checked);
  };

  const handleShowTextChange = (checked: boolean) => {
    if (!checked && !showOriginal) return;
    if (checked) setTextLoadFailed(false);
    setShowText(checked);
  };

  const handlePdfPageChange = useCallback((page: number, total: number) => {
    setPdfPageInfo({ page, total });
  }, []);

  const saveFile = async () => {
    if (!file) return;

    try {
      const success = await updateFiles([file]);
      if (success) {
        editableSnapshotRef.current = buildEditableSnapshot(file);
        pushNotification("File metadata saved successfully.", "success");
      } else {
        pushNotification("Failed to save file metadata.", "error");
      }
    } catch (error) {
      console.error("Error saving file metadata:", error);
      alert("An error occurred while saving file metadata.");
    }
  };

  const showBoth = showOriginal && showText;

  useEffect(() => {
    setPdfPageInfo(null);
  }, [docPath]);

  useEffect(() => {
    if (!showBoth) {
      setPdfPageInfo(null);
    }
  }, [showBoth]);

  /* ─── 4. Render ─────────────────────────────────────────── */
  if (!file) {
    return <Spinner size="xl" mt={10} />;
  }

  return (
    <BaseTemplate>
      <BaseTemplate.Header>
        <HStack>
          <Button onClick={saveAndClose}>Save and Close</Button>
          <Button onClick={saveAndNext}>Save and Next</Button>
          <Button onClick={next}>Skip</Button>
          <Button onClick={close}>Close</Button>
        </HStack>
      </BaseTemplate.Header>
      <BaseTemplate.Body>
        <Box h={"100%"}>
          <Flex>
            <Box w={"20%"} p={6} backgroundColor={"action-bar-bg"}>
              <FormControl mb={2}>
                <HStack spacing={4}>
                  <Checkbox
                    isChecked={showOriginal}
                    onChange={(e) => handleShowOriginalChange(e.target.checked)}
                  >
                    Show original
                  </Checkbox>
                  <Checkbox
                    isChecked={showText}
                    onChange={(e) => handleShowTextChange(e.target.checked)}
                  >
                    Show txt
                  </Checkbox>
                </HStack>
              </FormControl>

              <FormControl mb={2}>
                <FormLabel fontSize="xs" mb={0} color="gray.500">Status</FormLabel>
                <Input isReadOnly value={file.doc_status ?? ""} size="sm" variant="filled" />
              </FormControl>

              <FormControl mb={2}>
                <FormLabel fontSize="xs" mb={0} color="gray.500">Key ID</FormLabel>
                <HStack>
                  <Input isReadOnly value={file.key_id ?? ""} size="sm" variant="filled" />
                  <Button size="sm" onClick={onKeyIdModalOpen}>Change</Button>
                </HStack>
              </FormControl>

              <FormControl mb={2}>
                <FormLabel fontSize="xs" mb={0} color="gray.500">File Hash</FormLabel>
                <Input isReadOnly value={file.filehash ?? ""} size="sm" variant="filled" />
              </FormControl>

              <FormControl>
                <FormLabel htmlFor="docType">Doc Type</FormLabel>
                <Select
                  id="docType"
                  value={docType}
                  onChange={(e) => setDocType(e.target.value)}
                  {...keyFieldStyles}
                >
                  <option value="PDF">PDF</option>
                  <option value="CSV">CSV</option>
                  <option value="XLSX">XLSX</option>
                  <option value="TIF">TIF</option>
                  <option value="OTHER">OTHER</option>
                </Select>
              </FormControl>

              <FormControl>
                <FormLabel htmlFor="fromType">Customer or Supplier</FormLabel>
                <Select
                  id="fromType"
                  placeholder="Customer or Supplier"
                  value={customerSupplier ?? ""}
                  onChange={(e) => setCustomerSupplier(e.target.value)}
                  {...keyFieldStyles}
                >
                  <option value="customer">Customer</option>
                  <option value="supplier">Supplier</option>
                </Select>
              </FormControl>

              <FormControl>
                <FormLabel htmlFor="docFrom">
                  Doc From {editDefLink("doc_from")}
                </FormLabel>
                <Select
                  id="docFrom"
                  placeholder="Sender / Origin"
                  value={docFromShown}
                  onChange={(e) => handleFieldChange("doc_from", e.target.value)}
                  {...keyFieldStyles}
                >
                  {docFromShown && !docFromOptions.some((o) => o.value === docFromShown) && (
                    <option value={docFromShown}>{docFromShown} (not in the list)</option>
                  )}
                  {docFromOptions.length === 0 && (
                    <option value="" disabled>
                      Loading document families...
                    </option>
                  )}
                  {docFromOptions.length > 0 && (
                    <>
                      <option value="" disabled>
                        Select Document Family
                      </option>
                      {docFromOptions.map((option) => (
                        <option key={`${option.value} | ${option.label}`} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </>
                  )}
                </Select>
                {activeRegexField === "doc_from" && (
                  <RegexTester fieldKey="doc_from" onSave={regexSaveFor("doc_from")} onClose={() => setActiveRegexField(null)} initialValue={currentDocDef?.doc_from_regex} documentText={documentText} />
                )}
              </FormControl>

              <FormControl>
                <FormLabel htmlFor="docFamily">
                  Doc Family {editDefLink("doc_family")}
                </FormLabel>
                <Select
                  onChange={(e) => handleFieldChange("doc_family", e.target.value)}
                  value={file.doc_family ?? ""}
                  id="docFamily"
                  {...keyFieldStyles}
                >
                  {file.doc_family && !docFamilyOptions.some((o) => o.value === file.doc_family) && (
                    <option value={file.doc_family}>{file.doc_family} (not in the list)</option>
                  )}
                  {docFamilyOptions.length === 0 && (
                    <option value="" disabled>
                      Loading document families...
                    </option>
                  )}
                  {docFamilyOptions.length > 0 && (
                    <>
                      <option value="" disabled>
                        Select Document Family
                      </option>
                      {docFamilyOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </>
                  )}
                </Select>
                {activeRegexField === "doc_family" && (
                  <RegexTester fieldKey="doc_family" onSave={regexSaveFor("doc_family")} onClose={() => setActiveRegexField(null)} initialValue={currentDocDef?.doc_family_regex} documentText={documentText} />
                )}
              </FormControl>

              <FormControl>
                <FormLabel htmlFor="docRef">
                  Doc Ref {editDefLink("doc_ref")}
                </FormLabel>
                <Input
                  id="docRef"
                  placeholder="Reference # or code"
                  value={file.doc_ref ?? ""}
                  onChange={(e) => handleFieldChange("doc_ref", e.target.value)}
                  {...keyFieldStyles}
                />
                {activeRegexField === "doc_ref" && (
                  <RegexTester fieldKey="doc_ref" onSave={regexSaveFor("doc_ref")} onClose={() => setActiveRegexField(null)} initialValue={currentDocDef?.doc_ref_regex} documentText={documentText} />
                )}
              </FormControl>

              <FormControl pb={4} mb={1} borderBottom="1px solid" borderColor="gray.200">
                <FormLabel htmlFor="docDate">
                  Doc Date {editDefLink("doc_date")}
                </FormLabel>
                <Input
                  id="docDate"
                  type="date"
                  value={file.doc_date ?? ""}
                  onChange={(e) => handleFieldChange("doc_date", e.target.value)}
                  {...keyFieldStyles}
                  onPaste={(e) => {
                    const iso = toIso(e.clipboardData.getData("text"));
                    if (iso) {
                      e.preventDefault();
                      handleFieldChange("doc_date", iso);
                    }
                  }}
                />
                {activeRegexField === "doc_date" && (
                  <RegexTester fieldKey="doc_date" onSave={regexSaveFor("doc_date")} onClose={() => setActiveRegexField(null)} showDateFunction initialValue={currentDocDef?.doc_date_regex} initialDateFunction={currentDocDef?.doc_date_function} documentText={documentText} />
                )}
              </FormControl>

              {/* ───── Dates ───── */}
              <FormControl>
                <FormLabel htmlFor="docRelatedRef">
                  Related Doc Ref {editDefLink("doc_related_ref")}
                </FormLabel>
                <Input
                  id="docRelatedRef"
                  placeholder="Related reference # or code"
                  value={file.doc_related_ref ?? ""}
                  onChange={(e) => handleFieldChange("doc_related_ref", e.target.value)}
                />
                {activeRegexField === "doc_related_ref" && (
                  <RegexTester variant="multi" fieldKey="doc_related_ref" keyId={file?.doc_from ?? ""} keyName={parseFieldMap.doc_related_ref} onClose={() => setActiveRegexField(null)} documentText={documentText} />
                )}
              </FormControl>

              <FormControl>
                <FormLabel htmlFor="periodMagic">magic paste</FormLabel>
                <Input
                  id="periodMagic"
                  placeholder='Paste "PERIOD: 1 OCT 2023 TO 31 OCT 2023"'
                  value={periodPasteValue}
                  onChange={(e) => setPeriodPasteValue(e.target.value)}
                  onPaste={(e) => {
                    const text = e.clipboardData.getData("text");
                    const parsed = parsePeriodRange(text);
                    if (!parsed) return;
                    e.preventDefault();
                    setPeriodPasteValue(text.trim());
                    handleFieldChange("doc_period_from", parsed.from);
                    handleFieldChange("doc_period_to", parsed.to);
                  }}
                  onBlur={() => {
                    if (!periodPasteValue.trim()) return;
                    const parsed = parsePeriodRange(periodPasteValue);
                    if (!parsed) return;
                    handleFieldChange("doc_period_from", parsed.from);
                    handleFieldChange("doc_period_to", parsed.to);
                  }}
                />
              </FormControl>

              <FormControl>
                <FormLabel htmlFor="periodFrom">
                  Doc Period From {editDefLink("doc_period_from")}
                </FormLabel>
                <Input
                  id="periodFrom"
                  type="date"
                  value={file.doc_period_from ?? ""}
                  onChange={(e) => handleFieldChange("doc_period_from", e.target.value)}
                  onPaste={(e) => {
                    const iso = toIso(e.clipboardData.getData("text"));
                    if (iso) {
                      e.preventDefault();
                      handleFieldChange("doc_period_from", iso);
                    }
                  }}
                />
                {activeRegexField === "doc_period_from" && (
                  <RegexTester variant="multi" fieldKey="doc_period_from" keyId={file?.doc_from ?? ""} keyName={parseFieldMap.doc_period_from} onClose={() => setActiveRegexField(null)} documentText={documentText} />
                )}
              </FormControl>

              <FormControl>
                <FormLabel htmlFor="periodTo">
                  Doc Period To {editDefLink("doc_period_to")}
                </FormLabel>
                <Input
                  id="periodTo"
                  type="date"
                  value={file.doc_period_to ?? ""}
                  onChange={(e) => handleFieldChange("doc_period_to", e.target.value)}
                  onPaste={(e) => {
                    const iso = toIso(e.clipboardData.getData("text"));
                    if (iso) {
                      e.preventDefault();
                      handleFieldChange("doc_period_to", iso);
                    }
                  }}
                />
                {activeRegexField === "doc_period_to" && (
                  <RegexTester variant="multi" fieldKey="doc_period_to" keyId={file?.doc_from ?? ""} keyName={parseFieldMap.doc_period_to} onClose={() => setActiveRegexField(null)} documentText={documentText} />
                )}
              </FormControl>

              {/* ───── Amount ───── */}
              <FormControl>
                <FormLabel htmlFor="totalAmt">
                  Doc Total Amt Val {editDefLink("doc_total_amt_val")}
                </FormLabel>
                <NumberInput
                  id="totalAmt"
                  precision={2}
                  step={0.01}
                  value={file.doc_total_amt_val ?? ""}
                  onChange={(_, val) => handleFieldChange("doc_total_amt_val", val)}
                >
                  <NumberInputField
                    placeholder="0.00"
                    onPaste={(e) => {
                      const parsed = parseAmountFromText(e.clipboardData.getData("text"));
                      if (parsed === null) return;
                      e.preventDefault();
                      handleFieldChange("doc_total_amt_val", parsed);
                    }}
                  />
                </NumberInput>
                {activeRegexField === "doc_total_amt_val" && (
                  <RegexTester variant="multi" fieldKey="doc_total_amt_val" keyId={file?.doc_from ?? ""} keyName={parseFieldMap.doc_total_amt_val} onClose={() => setActiveRegexField(null)} documentText={documentText} />
                )}
              </FormControl>

              {/* ───── Account / site info ───── */}
              <FormControl>
                <FormLabel htmlFor="accountNumber">
                  Account Number {editDefLink("supplier_acct_ref")}
                </FormLabel>
                <Input
                  id="accountNumber"
                  placeholder="Account number"
                  value={file.supplier_acct_ref ?? ""}
                  onChange={(e) => handleFieldChange("supplier_acct_ref", e.target.value)}
                />
                {activeRegexField === "supplier_acct_ref" && (
                  <RegexTester variant="multi" fieldKey="supplier_acct_ref" keyId={file?.doc_from ?? ""} keyName={parseFieldMap.supplier_acct_ref} onClose={() => setActiveRegexField(null)} documentText={documentText} />
                )}
              </FormControl>

              <FormControl>
                <FormLabel htmlFor="crmAccount">CRM Account</FormLabel>

                <AsyncSelect
                  /* ------------- basics ------------- */
                  inputId="crmAccount"
                  placeholder="Start typing…"
                  cacheOptions
                  defaultOptions
                  loadOptions={loadCrmAccountOptions}
                  /* ------------- key + label helpers ------------- */
                  getOptionValue={(o) => o.value} // ID is the unique key
                  getOptionLabel={(o) => o.label} // name is what user sees
                  /* ------------- controlled value ------------- */
                  value={selectedCrm}
                  onChange={(opt) => {
                    const option = opt as CrmOption | null;

                    // keep the select controlled
                    setSelectedCrm(option);

                    // preserve your existing behavior + set CRM-specific fields too
                    setFile((prev) =>
                      prev
                        ? {
                          ...prev,
                          account_id_crm: option?.value ?? "",
                          account_name: option?.label ?? "",
                        }
                        : prev
                    );
                  }}
                  isClearable={false}
                  styles={{ menu: (p) => ({ ...p, zIndex: 10 }) }}
                />
              </FormControl>

              <HStack mt={4}>
                <Button variant="actionBar" onClick={saveFile}>
                  Save Metadata
                </Button>
                <Button
                  size="sm"
                  colorScheme="orange"
                  onClick={startRescan}
                  leftIcon={isRescanning ? <Spinner size="xs" /> : undefined}
                >
                  Rescan
                </Button>
              </HStack>
              <HStack mt={2} align="center">
                <Button size="sm" onClick={handleRefresh} isLoading={isRefreshing} flexShrink={0}>
                  Refresh
                </Button>
                <Text fontSize="xs" color="gray.500">
                  Last scan: {scanTimeText(file.parsedate)}
                  {isRescanning
                    ? " · rescanning"
                    : scantimeAtRescan !== null &&
                      ((file.parsedate ?? "") === scantimeAtRescan ? " · rescan queued, not run yet" : " · rescanned")}
                </Text>
              </HStack>
            </Box>
            <Box
              backgroundColor={"action-bar-bg"}
              p={2}
              border={"1px"}
              borderRadius={"12px"}
              borderColor={"action-bar-border"}
              boxShadow={"md"}
              w={"80%"}
              h={"100%"}
            >
              <Flex h="100%" gap={showBoth ? 4 : 0}>
                {showOriginal && (
                  <Box flex="1" h="100%">
                    {originalFileBlob && originalFileName ? (
                      <Viewer
                        fileBlob={originalFileBlob}
                        fileName={originalFileName}
                        onPdfPageChange={showBoth ? handlePdfPageChange : undefined}
                      />
                    ) : (
                      <Flex align="center" justify="center" h="100%">
                        <Spinner size="lg" />
                      </Flex>
                    )}
                  </Box>
                )}
                {showText && (
                  <Box flex="1" h="100%">
                    {textFileBlob && textFileName ? (
                      <Viewer
                        fileBlob={textFileBlob}
                        fileName={textFileName}
                        scrollToPage={showBoth ? pdfPageInfo : null}
                      />
                    ) : (
                      <Flex align="center" justify="center" h="100%">
                        <Spinner size="lg" />
                      </Flex>
                    )}
                  </Box>
                )}
              </Flex>
            </Box>
          </Flex>
        </Box>
      </BaseTemplate.Body>
      <>
        <AlertDialog
          motionPreset="slideInBottom"
          leastDestructiveRef={cancelRef}
          onClose={onClose}
          isOpen={isOpen}
          isCentered
        >
          <AlertDialogOverlay />

          <AlertDialogContent>
            <AlertDialogHeader>Discard Changes?</AlertDialogHeader>
            <AlertDialogCloseButton />
            <AlertDialogBody>
              {discardMessage}
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onClose}>
                No
              </Button>
              <Button
                colorScheme="red"
                ml={3}
                onClick={() => {
                  onClose();
                  if (pendingDiscardAction === "next") {
                    next();
                  } else if (pendingDiscardAction === "refresh") {
                    refreshFile();
                  } else {
                    window.close();
                  }
                  setPendingDiscardAction(null);
                }}
              >
                Yes
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <Modal
          isOpen={rescanPopup !== "closed"}
          onClose={() => setRescanPopup("closed")}
          isCentered
          size="sm"
          closeOnOverlayClick={rescanPopup === "queued"}
        >
          <ModalOverlay />
          <ModalContent>
            <ModalBody pt={6} pb={2}>
              {rescanPopup === "queued" ? (
                <Text fontWeight="semibold">Rescan has been queued</Text>
              ) : (
                <HStack spacing={4} align="start">
                  <Spinner color="orange.400" thickness="3px" mt={1} flexShrink={0} />
                  <Box>
                    <Text fontWeight="semibold">
                      {rescanAskingClaude
                        ? "Asking Claude for help with this ... please wait"
                        : "Rescanning ... please wait"}
                    </Text>
                    {rescanAskingClaude ? (
                      <>
                        <Text fontSize="sm" color="gray.500" mt={3}>
                          No parsing template recognises this layout.
                        </Text>
                        <Text fontSize="sm" color="gray.500" mt={3}>
                          Claude is reading the document and attempting to create a template ...
                        </Text>
                      </>
                    ) : (
                      <Text fontSize="sm" color="gray.500" mt={1}>
                        Reading the document again with the current rules.
                      </Text>
                    )}
                    <Text fontSize="xs" color="gray.400" mt={2}>
                      {rescanElapsed}s
                    </Text>
                  </Box>
                </HStack>
              )}
            </ModalBody>
            <ModalFooter pt={2}>
              <Button size="sm" variant="ghost" onClick={() => setRescanPopup("closed")}>
                {rescanPopup === "queued" ? "OK" : "Hide"}
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>

        {file && (
          <KeyIdSelectorModal
            isOpen={isKeyIdModalOpen}
            onClose={onKeyIdModalClose}
            sid={file.sid}
            onAssigned={(keyId) => setFile((f) => f ? { ...f, key_id: keyId } : f)}
          />
        )}
      </>
    </BaseTemplate>
  );
}
